
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigServerService } from './server';
import { CustomHttp } from '../http';
import { Config, ConfigEmpresa, ConfigMeta, ConfigAnaliseMercado, ConfigCotacao, ConfigCampanha, ConfigTabelaPreco, ConfigPedido, ConfigPedidoTipoVenda, ConfigCliente, ConfigItem, ConfigTituloReceber, ConfigNotaFiscal, ConfigUnidadeNegocio, ConfigAtividade } from '../../../home/configuracoes/app';
import { SessionStorageService } from '../web-storage';

const configKey = 'config';

/**
 * Service que carrega a configuração da aplicação
 */
@Injectable({
    providedIn: 'root'
})
export class ConfigService {
    private pathApi
    constructor(configServerService: ConfigServerService,
                private customHttp: CustomHttp,
                private sessionStorageService: SessionStorageService) {
        this.pathApi = configServerService.getPathApiConfig();
    }

    /**
     * Carrega as configurações a aplicação
     */
    load(): Observable<Config> {
        return this.customHttp.get<Config>(`${this.pathApi}/load`)
                    .pipe(
                        map(response => {
                            // Adicionando no SessionStorage as configurações da aplicação
                            this.sessionStorageService.setObject(configKey, response);
                            return response;
                        })
                    );
    }

    /** Configuração da aplicação */
    getConfig(): Config {
        return this.sessionStorageService.getObject(configKey) as Config;
    }

    /** Configuração da Empresa */
    getEmpresa(): ConfigEmpresa {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.empresa;
    }

    /** Configuração da Meta */
    getMeta(): ConfigMeta {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.meta;
    }

    /** Configuração da Análise de Mercado */
    getAnaliseMercado(): ConfigAnaliseMercado {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.analiseMercado;
    }

    /** Configuração da Cotação */
    getCotacao(): ConfigCotacao {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.cotacao;
    }

    /** Configuração das Campanhas */
    getCampanha(): ConfigCampanha {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.campanha;
    }

    /** Configuração das tabelas de preços */
    getTabelaPreco(): ConfigTabelaPreco {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.tabelaPreco;
    }

    /** Configuração do pedido */
    getPedido(): ConfigPedido {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.pedido;
    }

    /** Configurações dos tipos dos pedidos */
    getPedidoTipos(): ConfigPedidoTipoVenda[] {
        const configPedido = this.getPedido();
        return configPedido?.tiposVenda ?? null;
    }

    /** Tenta obter a configuração do tipo do pedido */
    getPedidoTipo(tipo: number): ConfigPedidoTipoVenda {
        const configTipos = this.getPedidoTipos();
        if (!configTipos) {
            return null;
        }

        for (let i = 0; i < configTipos.length; i++) {
            const configTipo = configTipos[i];
            if (configTipo.tipoVenda === tipo) {
                return configTipo;
            }
        }

        return null;
    }

    /** Configuração do cliente */
    getCliente(): ConfigCliente {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.cliente;
    }

    /** Configuração do item */
    getItem(): ConfigItem {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.item;
    }

    /** Configuração do título a receber */
    getTituloReceber(): ConfigTituloReceber {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.tituloReceber;
    }

    /** Configuração da nota fiscal */
    getNotaFiscal(): ConfigNotaFiscal {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.notaFiscal;
    }

    /** Configuração da unidade de negócio */
    getUnidadeNegocio(): ConfigUnidadeNegocio {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.unidadeNegocio;
    }

    /** Configuração da atividade do tipo entrar em contato */
    getAtividadeEntrarEmContato(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeEntrarEmContato;
    }

    /** Configuração da atividade do tipo visitar */
    getAtividadeVisitar(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeVisitar;
    }

    /** Configuração da atividade do tipo tarefa */
    getAtividadeTarefa(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeTarefa;
    }

    /** Configuração da atividade do tipo cliente novo */
    getAtividadeClienteNovo(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeClienteNovo;
    }

    /** Configuração da atividade do tipo cliente inativo */
    getAtividadeClienteInativo(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeClienteInativo;
    }

    /** Configuração da atividade do tipo cliente pré-inativo */
    getAtividadeClientePreInativo(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeClientePreInativo;
    }

    /** Configuração da atividade do tipo cliente revisão */
    getAtividadeClienteRevisao(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeClienteRevisao;
    }

    /** Configuração da atividade do tipo cliente título vencido */
    getAtividadeClienteTituloVencido(): ConfigAtividade {
        const config = this.getConfig();
        if (!config) {
            return null;
        }
        return config.atividadeClienteTituloVencido;
    }
}
